import { NODE_TYPES } from './constants';

export const uid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;

/**
 * The graph the canvas draws is derived from the same `blocks` array the Ruby
 * engine walks — connections live inside each block's options rather than in a
 * separate edge list. These helpers are the single place that knows how a
 * handle id maps onto the option that stores it.
 */

const inputMessage = block =>
  (block.options?.messages || []).find(
    message => message.name === 'text-input-message'
  );

const buttonsOf = block =>
  (block.options?.messages || []).flatMap(
    message => message.options?.buttons || []
  );

/** Outgoing connection points of a node, in the order they should render. */
export function sourceHandlesFor(block) {
  const options = block.options || {};

  switch (block.type) {
    case 'chat': {
      const handles = buttonsOf(block).map(button => ({
        id: `button:${button.id}`,
        label: button.text,
        labelKey: button.text ? null : 'FLOWS.HANDLES.BUTTON',
        tone: 'brand',
      }));
      handles.push({
        id: 'next',
        labelKey: 'FLOWS.HANDLES.NEXT',
        tone: 'default',
      });
      if (inputMessage(block)) {
        handles.push({
          id: 'timeout',
          labelKey: 'FLOWS.HANDLES.TIMEOUT',
          tone: 'muted',
        });
      }
      return handles;
    }
    case 'condition': {
      const handles = (options.conditions || [])
        .filter(condition => condition.nextBlockId !== undefined)
        .map(condition => ({
          id: `condition:${condition.id}`,
          labelKey: 'FLOWS.HANDLES.MATCH',
          tone: 'brand',
        }));
      handles.push({
        id: 'true',
        labelKey: 'FLOWS.HANDLES.TRUE',
        tone: 'success',
      });
      handles.push({
        id: 'false',
        labelKey: 'FLOWS.HANDLES.FALSE',
        tone: 'danger',
      });
      return handles;
    }
    case 'randomizer':
      return (options.randomizers || []).map(branch => ({
        id: `branch:${branch.id}`,
        label: `${branch.name || ''} · ${branch.perc || 0}%`.replace(
          /^ · /,
          ''
        ),
        labelKey: branch.name ? null : 'FLOWS.HANDLES.PATH',
        tone: 'brand',
      }));
    case 'action':
    case 'api':
    case 'field-operation':
      return [
        { id: 'next', labelKey: 'FLOWS.HANDLES.NEXT', tone: 'default' },
        { id: 'error', labelKey: 'FLOWS.HANDLES.ERROR', tone: 'danger' },
      ];
    case 'ai-agent': {
      const aiHandles = [];
      const routes = options.action_routes || {};
      Object.keys(routes).forEach(action => {
        aiHandles.push({
          id: `ai-action:${action}`,
          label: action,
          tone: 'brand',
        });
      });
      aiHandles.push({
        id: 'timeout',
        label: 'Timeout',
        tone: 'muted',
      });
      aiHandles.push({
        id: 'error',
        labelKey: 'FLOWS.HANDLES.ERROR',
        tone: 'danger',
      });
      return aiHandles;
    }
    default:
      return [{ id: 'next', labelKey: 'FLOWS.HANDLES.NEXT', tone: 'default' }];
  }
}

/** Reads the block id a handle currently points at. */
export function readConnection(block, handleId) {
  const options = block.options || {};

  if (handleId === 'next') return options.nextBlockId || '';
  if (handleId === 'error') return options.errorNextBlockId || '';
  if (handleId === 'true') return options.trueNextBlockId || '';
  if (handleId === 'false') return options.falseNextBlockId || '';
  if (handleId === 'timeout')
    return inputMessage(block)?.options?.timeoutNextBlockId || '';

  const [kind, key] = handleId.split(':');
  if (kind === 'button')
    return buttonsOf(block).find(b => b.id === key)?.nextBlockId || '';
  if (kind === 'condition')
    return (
      (options.conditions || []).find(c => c.id === key)?.nextBlockId || ''
    );
  if (kind === 'branch')
    return (
      (options.randomizers || []).find(r => r.id === key)?.nextBlockId || ''
    );
  if (kind === 'ai-action')
    return (options.action_routes || {})[key] || '';

  return '';
}

/** Points a handle at a block (pass '' to disconnect). Mutates in place. */
export function writeConnection(block, handleId, targetId) {
  const value = targetId || '';
  if (!block.options) block.options = {};
  const { options } = block;

  const direct = {
    next: 'nextBlockId',
    error: 'errorNextBlockId',
    true: 'trueNextBlockId',
    false: 'falseNextBlockId',
  }[handleId];

  if (direct) {
    options[direct] = value;
    return block;
  }

  if (handleId === 'timeout') {
    const input = inputMessage(block);
    if (input) {
      input.options = input.options || {};
      input.options.timeoutNextBlockId = value;
    }
    return block;
  }

  const [kind, key] = handleId.split(':');

  if (kind === 'ai-action') {
    if (!options.action_routes) options.action_routes = {};
    options.action_routes[key] = value;
    return block;
  }

  const collections = {
    button: buttonsOf(block),
    condition: options.conditions || [],
    branch: options.randomizers || [],
  };

  const target = (collections[kind] || []).find(item => item.id === key);
  if (target) target.nextBlockId = value;
  return block;
}

export function blocksToNodes(blocks, blockStats = {}) {
  return blocks.map(block => ({
    id: block.id,
    type: block.type === 'sticky' ? 'stickyNote' : 'flowBlock',
    position: { x: block.presentation?.x ?? 0, y: block.presentation?.y ?? 0 },
    data: { block, stats: blockStats[block.id] || { success: 0, alert: 0, error: 0 } },
  }));
}

export function blocksToEdges(blocks) {
  const known = new Set(blocks.map(block => block.id));

  return blocks.flatMap(block =>
    sourceHandlesFor(block)
      .map(handle => ({ handle, target: readConnection(block, handle.id) }))
      // Drop dangling links so a deleted node cannot leave a floating edge.
      .filter(({ target }) => target && known.has(target))
      .map(({ handle, target }) => ({
        id: `${block.id}__${handle.id}`,
        source: block.id,
        sourceHandle: handle.id,
        target,
        targetHandle: 'in',
        type: 'smoothstep',
        animated: false,
        style: { borderRadius: 15 },
        data: { tone: handle.tone },
      }))
  );
}

export function createBlock(type, position) {
  if (type === 'sticky') {
    return {
      id: uid(),
      type: 'sticky',
      presentation: { x: Math.round(position.x), y: Math.round(position.y) },
      options: { text: '', color: 'yellow' },
    };
  }
  const definition = NODE_TYPES[type];
  return {
    id: uid(),
    type,
    presentation: { x: Math.round(position.x), y: Math.round(position.y) },
    options: definition.defaults(),
  };
}

/** Removes a block and every connection pointing at it. */
export function removeBlock(blocks, blockId) {
  return blocks
    .filter(block => block.id !== blockId)
    .map(block => {
      sourceHandlesFor(block).forEach(handle => {
        if (readConnection(block, handle.id) === blockId)
          writeConnection(block, handle.id, '');
      });
      return block;
    });
}
